import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HistoryIcon from "@mui/icons-material/History";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import moment from "moment";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { getBlockStatus } from "../../apis/blocking/blocking-apis";
import {
  getActiveCoupons,
  getPayments,
  getTrips,
} from "../../apis/customerProfile/apis";
import type { BlockReason, UserStatus } from "../../domain/blocking";
import { strings } from "../../I18n";
import { modifyCurrencyToSymbolAndValue } from "../../utils/utils";
import BlockUserModal from "../modal/blockUserModal";
import UnblockUserModal from "../modal/unblockUserModal";
import BlockHistoryModal from "./BlockHistoryModal";

interface Props {
  user?: any;
  userDetailInfo?: any;
  walletData?: any;
}

const CustomerDetails: React.FC<Props> = ({
  user,
  userDetailInfo,
  walletData,
}) => {
  const [activityData, setActivityData] = useState<any>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [lastGatewayPayment, setLastGatewayPayment] = useState<any>(null);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [unblockModalOpen, setUnblockModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [blockStatus, setBlockStatus] = useState<UserStatus>(
    user?.status || "ACTIVE"
  );
  const [blockReason, setBlockReason] = useState<BlockReason | undefined>();

  const fetchBlockStatus = async () => {
    if (!user?.id) return;
    try {
      const status = await getBlockStatus(user.id.toString());
      setBlockStatus(status.status || "ACTIVE");
      setBlockReason(status.blockReason);
    } catch (e) {
      console.error("Failed to fetch block status", e);
    }
  };

  const handleBlockSuccess = () => {
    setBlockStatus("BLOCKED");
    fetchBlockStatus();
  };

  const handleUnblockSuccess = () => {
    setBlockStatus("ACTIVE");
    setBlockReason(undefined);
  };

  useEffect(() => {
    fetchBlockStatus();
  }, [user?.id]);

  // Filter wallet transactions to only include actual top-ups (money in)
  const actualTopUps = useMemo(() => {
    const transactions = walletData?.unifiedTransactions || [];
    return transactions
      .filter((txn: any) => {
        // Only count ADD transactions from payment configurations (top-ups)
        if (txn.type !== "ADD") return false;
        if (!txn.reason?.type) return false;
        return txn.reason.type === "PAYMENT_CONFIGURATION_TRANSACTION";
      })
      .sort(
        (a: any, b: any) =>
          new Date(b.creationDatetime).getTime() -
          new Date(a.creationDatetime).getTime()
      );
  }, [walletData]);

  const lastUnifiedPayment = useMemo(() => {
    const txns = walletData?.unifiedTransactions || [];
    if (!txns.length) return null;
    const completed = txns
      .filter((txn: any) => (txn.status || "").toUpperCase() === "COMPLETED")
      .sort(
        (a: any, b: any) =>
          new Date(b.creationDatetime).getTime() -
          new Date(a.creationDatetime).getTime()
      );
    return completed[0] || null;
  }, [walletData]);

  const getReasonLabel = (txn: any) => {
    const reasonType = txn?.reason?.type;
    if (reasonType === "PAYMENT_CONFIGURATION_TRANSACTION") return "Top-up";
    if (reasonType === "TRIP") return "Trip";
    if (reasonType === "ADMIN_INTERVENTION") return "Adjustment";
    return txn?.type || "-";
  };

  useEffect(() => {
    const fetchActivityData = async () => {
      if (!userDetailInfo?.userId) return;

      setLoadingActivity(true);
      try {
        const [tripsData, paymentsData] = await Promise.all([
          getTrips({ userId: userDetailInfo.userId, page: 0, size: 1000 }),
          getPayments({ userId: userDetailInfo.userId, page: 0, size: 1000 }),
        ]);

        // Fetch active coupons if we have an active city
        let activeCoupons: any[] = [];
        if (userDetailInfo?.activeCityId) {
          activeCoupons = await getActiveCoupons(
            userDetailInfo.userId,
            userDetailInfo.activeCityId
          );
        }

        // Count coupons used from trips
        const couponsUsed = tripsData.content.filter(
          (trip) => trip.couponId
        ).length;

        // Count actual payments (amount > 0) from payment gateway
        const actualPayments = paymentsData.content.filter(
          (p) => p.status === "CAPTURED" && p.amount > 0
        );
        setLastGatewayPayment(actualPayments[0] || null);

        // Combine wallet top-ups and actual payments
        const allPayments = [...actualTopUps, ...actualPayments].sort(
          (a: any, b: any) => {
            const aDate = a.creationDatetime || a.dateCreated;
            const bDate = b.creationDatetime || b.dateCreated;
            return new Date(bDate).getTime() - new Date(aDate).getTime();
          }
        );

        const daysSinceMember = user?.dateCreated
          ? moment().diff(moment(user.dateCreated), "days")
          : null;

        setActivityData({
          totalTrips: tripsData.totalElements,
          lastTrip: tripsData.content?.[0],
          totalPayments: allPayments.length,
          lastPayment: allPayments[0],
          couponsUsed,
          activeCoupon: activeCoupons[0],
          daysSinceMember,
        });
      } catch (error) {
        console.error("Failed to fetch activity data:", error);
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchActivityData();
  }, [
    userDetailInfo?.userId,
    userDetailInfo?.activeCityId,
    user?.dateCreated,
    actualTopUps,
  ]);

  return (
    <Grid container={true} spacing={3}>
      <Grid item={true} xs={12} md={6}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {strings("descriptions.accountStatus")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {strings("descriptions.creationDate")}
                </Typography>
                <Typography variant="body2">
                  {user?.dateCreated
                    ? moment(user.dateCreated).format("DD.MM.YYYY HH:mm")
                    : "-"}
                </Typography>
              </Box>
              {user?.birthDate && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Date of Birth
                  </Typography>
                  <Typography variant="body2">
                    {moment(user.birthDate).format("DD.MM.YYYY")}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {strings("descriptions.emailVerified")}
                </Typography>
                <Chip
                  icon={user?.emailVerified ? <CheckCircleIcon /> : undefined}
                  label={user?.emailVerified ? "Verified" : "Unverified"}
                  size="small"
                  color={user?.emailVerified ? "success" : "default"}
                  variant={user?.emailVerified ? "outlined" : "filled"}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {strings("descriptions.status")}
                </Typography>
                <Chip
                  label={blockStatus}
                  size="small"
                  color={
                    blockStatus === "ACTIVE"
                      ? "success"
                      : blockStatus === "BLOCKED"
                        ? "error"
                        : "default"
                  }
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Actions
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {blockStatus === "BLOCKED" ? (
                    <Typography
                      variant="body2"
                      component="span"
                      onClick={() => setUnblockModalOpen(true)}
                      sx={{
                        color: "success.main",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Unblock
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      component="span"
                      onClick={() => setBlockModalOpen(true)}
                      sx={{
                        color: "error.main",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Block
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    ·
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    onClick={() => setHistoryModalOpen(true)}
                    sx={{
                      color: "text.secondary",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    History
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {strings("descriptions.latestActiveCity")}
                </Typography>
                <Typography variant="body2">
                  {userDetailInfo?.activeCityName || "-"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Stripe
                </Typography>
                {user?.stripeProfileUrl ? (
                  <Typography
                    variant="body2"
                    component="a"
                    href={user.stripeProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "#1976d2", textDecoration: "none" }}
                  >
                    {strings("descriptions.viewStripeProfile")}
                  </Typography>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item={true} xs={12} md={6}>
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Activity & Statistics
            </Typography>
            {loadingActivity ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : activityData ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Total Trips
                  </Typography>
                  <Chip
                    label={activityData.totalTrips}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                {activityData.lastTrip && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Last Trip
                    </Typography>
                    <Typography variant="body2">
                      {moment(activityData.lastTrip.startTime).format(
                        "DD.MM.YYYY HH:mm"
                      )}
                    </Typography>
                  </Box>
                )}
                {walletData?.migratedUser && lastUnifiedPayment && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Last payment
                    </Typography>
                    <Typography variant="body2">
                      {modifyCurrencyToSymbolAndValue(
                        walletData?.unifiedWallet?.currency || "",
                        lastUnifiedPayment.amountMinor / 100
                      )}{" "}
                      · {getReasonLabel(lastUnifiedPayment)}
                    </Typography>
                  </Box>
                )}
                {!walletData?.migratedUser && lastGatewayPayment && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Last payment
                    </Typography>
                    <Typography variant="body2">
                      {modifyCurrencyToSymbolAndValue(
                        lastGatewayPayment.currency ||
                          walletData?.unifiedWallet?.currency ||
                          "",
                        lastGatewayPayment.amount / 100
                      )}{" "}
                      · {lastGatewayPayment.status}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Total Payments
                  </Typography>
                  <Chip
                    label={activityData.totalPayments}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
                {activityData.lastPayment && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Last Payment
                    </Typography>
                    <Typography variant="body2">
                      {moment(
                        activityData.lastPayment.creationDatetime ||
                          activityData.lastPayment.dateCreated
                      ).format("DD.MM.YYYY HH:mm")}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Coupons Used
                  </Typography>
                  <Chip
                    label={activityData.couponsUsed}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Box>
                {activityData.activeCoupon && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Active Coupon
                    </Typography>
                    <Chip
                      label={
                        activityData.activeCoupon.code ||
                        activityData.activeCoupon.name ||
                        "Active"
                      }
                      size="small"
                      color="warning"
                    />
                  </Box>
                )}
                {activityData.daysSinceMember !== null && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Member For
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {activityData.daysSinceMember} days
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No activity data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {user?.id && (
        <>
          <BlockUserModal
            open={blockModalOpen}
            onClose={() => setBlockModalOpen(false)}
            userId={user.id.toString()}
            userName={user.fullName || user.email || "User"}
            onSuccess={handleBlockSuccess}
          />

          <UnblockUserModal
            open={unblockModalOpen}
            onClose={() => setUnblockModalOpen(false)}
            userId={user.id.toString()}
            userName={user.fullName || user.email || "User"}
            currentReason={blockReason}
            onSuccess={handleUnblockSuccess}
          />

          <BlockHistoryModal
            open={historyModalOpen}
            onClose={() => setHistoryModalOpen(false)}
            userId={user.id.toString()}
          />
        </>
      )}
    </Grid>
  );
};

export default CustomerDetails;
